import { Injectable } from '@angular/core';
import { BillyService } from '../billyService/billy.service';
import { QuotationsService } from '../quotations/quotations.service';
import { IQuota } from 'src/app/@interfaces/quotation.interface';
import { IContFact } from 'src/app/@interfaces/contfac.interface';
import { IVentaQuery } from 'src/app/@interfaces/venta.interface';
import { IGraphQToBilly } from 'src/app/@interfaces/Billy/billy.interface';
import { Driver } from 'src/app/@interfaces/driver.interface';
import { Subject } from 'rxjs';

import { takeUntil } from 'rxjs/operators';
import { OperationalReportingService } from '../reports/pages/operational-reporting/operational-reporting.service';
import {
  NgxQrcodeElementTypes,
  NgxQrcodeErrorCorrectionLevels,
} from 'src/app/@shared/components/NgxQRCode/qrcode.types';
import { GenerateLetterheadPdfService } from './details-pdf-letterhead.service';
import { STRING_CONSTANTS } from '@shared-constants';
import { environment } from 'src/environments/environment';
import { ElectronicInvoicePdfService } from './electronic-invoice-pdf.service';
import { InvoicePdf } from '@shared-models';

@Injectable({
  providedIn: 'root',
})
export class CalculateDetailsService {
  subtotal: number = 0;
  taxCalculated: number = 0;
  otherTaxes: number = 0;
  discount: number = 0;
  total: number = 0;

  elementType = NgxQrcodeElementTypes.URL;
  correctionLevel = NgxQrcodeErrorCorrectionLevels.HIGH;
  qrUrl = '';

  invoiceObject = {} as IQuota;
  invoiceCount = {} as IContFact;
  sales: Array<IVentaQuery> = [];
  billyInvoiceData? = {} as IGraphQToBilly;

  driverObject = {} as Driver;
  rateBCV: number | undefined;

  unsubscribe$ = new Subject<void>();
  constructor(
    private billy: BillyService,
    private quotationService: QuotationsService,
    private operationalReportingService: OperationalReportingService,
    private generateLetterheadPdfService: GenerateLetterheadPdfService,
    private electronicInvoicePdfService: ElectronicInvoicePdfService,
  ) {}

  async setDocument(invoiceID: number, company: string, type?: string) {
    this.subtotal = 0;
    this.taxCalculated = 0;
    this.otherTaxes = 0;
    this.discount = 0;
    this.total = 0;
    this.invoiceObject = await this.getInvoice(invoiceID, company);
    this.qrUrl = await this.setQRCode(invoiceID, company, type! || '1');
    this.invoiceCount = this.invoiceObject.contfac!;
    this.sales = this.invoiceObject.venta;
    this.sales.forEach((element) => {
      const saleValues = this.calculateTotals(
        element,
        this.invoiceObject.tax_incl!,
      );
      this.taxCalculated += saleValues.calculatedTax;
      this.otherTaxes += saleValues.calculatedOtherTax;
      this.subtotal += saleValues.subtotal;
      this.discount += saleValues.discount;
      this.total += saleValues.totals;
    });
    this.driverObject = await this.setDriverData(
      this.invoiceObject.num_fac,
      company,
    );
    this.rateBCV = this.driverObject?.tasa_c! || 0;
    if (type) {
      await this.generatePDFByType(type, company);
    }
  }

  async generatePDFByType(type: string, company: string) {
    const invoiceObject: InvoicePdf = {
      open: true,
      invoiceObject: this.invoiceObject,
      invoiceCount: this.invoiceCount,
      sales: this.sales,
      subtotal: this.subtotal,
      taxCalculated: this.taxCalculated,
      otherTaxCalculated: this.otherTaxes,
      discount: this.discount,
      total: this.total,
      qrUrl: this.qrUrl,
      rateBCV: this.rateBCV,
      driverObject: this.driverObject,
      company: company,
    };
    if (type === '1') {
      await this.electronicInvoicePdfService.createInvoicePdf(invoiceObject);
    } else {
      this.generateLetterheadPdfService.createInvoiceLetterheadPdf(
        invoiceObject,
      );
    }
  }

  calculateTotals(element: IVentaQuery, taxIncluded: string) {
    let discount = 0;
    let subtotal = 0;
    let totals = 0;
    let calculatedTax = 0;
    let calculatedOtherTax = 0;
    let tax = 0;
    let price = 0;

    if (taxIncluded === STRING_CONSTANTS.TRUE) {
      // Case: price already includes tax
      tax = element.articulo[0].tax![0].value;
      price = element.precio / (tax + 1);
      subtotal = element.cantidad * price;
      discount = (element.descuento / 100) * subtotal;
    } else {
      // Case: price excluding tax
      price = element.precio;
      discount = (element.descuento / 100) * (element.cantidad * price);
      tax = this.operationalReportingService.getTaxPercent(
        element,
        this.invoiceObject,
      );
    }
    subtotal = element.cantidad * price;
    subtotal = subtotal - discount;

    // 01 is the code for IVA, so all other taxes that are not IVA will be calculated.
    if (element.articulo[0].tax![0].dian_code !== '01') {
      calculatedOtherTax = subtotal * tax;
    } else {
      calculatedTax = subtotal * tax;
    }

    totals = subtotal + calculatedTax;

    return {
      calculatedTax,
      calculatedOtherTax,
      subtotal,
      discount,
      totals,
    };
  }

  getInvoice(invoiceID: number, company: string) {
    return new Promise<IQuota>((resolve, reject) => {
      this.quotationService
        .getQuotation(invoiceID, company)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe((invoice) => {
          resolve(invoice);
        });
    });
  }

  async setQRCode(invoiceID: number, company: string, type: string) {
    if (this.invoiceObject.estado === 4 && company === '9') {
      return await this.setBillyQR(
        this.invoiceObject.billyInvoice!,
        invoiceID,
        company,
        type,
      );
    } else {
      return this.generateDefaultQr(invoiceID, company, type);
    }
  }

  async setBillyQR(
    billyGraphQL: IGraphQToBilly,
    invoiceID: number,
    company: string,
    type: string,
  ) {
    return new Promise<string>((resolve, reject) => {
      this.billy
        .getInvoice(billyGraphQL!.id_billy.toString(), company)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe(
          (result) => {
            this.billyInvoiceData = billyGraphQL;
            resolve(result.data.attributes.pdfUrl);
          },
          (error) => {
            this.billyInvoiceData = undefined;
            resolve(this.generateDefaultQr(invoiceID, company, type));
          },
        );
    });
  }

  generateDefaultQr(invoiceID: number, company: string, type: string) {
    if (this.invoiceObject.billyInvoice?.cufe) {
      return `${environment.dian_vpfe}/document/searchqr?documentkey=${this.invoiceObject.billyInvoice?.cufe}`;
    }
    return `https://v1-insight.com/#/details/${invoiceID}/${company}/${type}`;
  }

  async setDriverData(num_fac: string, company: string) {
    return new Promise<Driver>((resolve, reject) => {
      this.quotationService
        .getDriver(num_fac, company)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe(
          (result) => {
            if (result) {
              resolve(result);
            } else {
              resolve(this.setNullDriver());
            }
          },
          (error) => {
            reject(this.setNullDriver());
          },
        );
    });
  }

  setNullDriver(): Driver {
    return {
      chofer: '-',
      cin: '-',
      destino: '-',
      id_chofer: 0,
      invoice: '-',
      permiso_rasda: '-',
      peso_neto: '-',
      placa: '-',
      son: '-',
      tasa_c: 0,
    };
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
